import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { RotateLeft } from '@sorare/core/src/atoms/icons/RotateLeft';
import { Vault } from '@sorare/core/src/atoms/icons/Vault';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import { TokenLoyaltyBoost } from '../TokenLoyaltyBoost';
import type { TokenSealingFooter_anyCard } from './__generated__/index.graphql';

type Props = {
  anyCard: TokenSealingFooter_anyCard;
  selected?: boolean;
  onSelect?: () => void;
};

export const TokenSealingFooter = ({
  anyCard,
  selected = false,
  onSelect,
}: Props) => {
  const { sport } = useSportContext();
  const { sealableFor, sealed } = anyCard;

  if (sport === Sport.BASEBALL) {
    return <TokenLoyaltyBoost anyCard={anyCard} selected={selected} />;
  }

  if (sealed) {
    return null;
  }

  if (selected) {
    return (
      <Button size="small" color="quaternary" onClick={onSelect}>
        <RotateLeft />
        <FormattedMessage {...glossary.undo} />
        <span>({sealableFor})</span>
      </Button>
    );
  }

  return (
    <Button size="small" color="quaternary" onClick={onSelect}>
      <Vault size={2} action="vault" />
      <Horizontal>{sealableFor}</Horizontal>
    </Button>
  );
};

TokenSealingFooter.fragments = {
  anyCard: gql`
    fragment TokenSealingFooter_anyCard on AnyCardInterface {
      slug
      sealed
      sealableFor
      ...TokenLoyaltyBoost_anyCard
    }
    ${TokenLoyaltyBoost.fragments.anyCard}
  ` as TypedDocumentNode<TokenSealingFooter_anyCard>,
};
